import React, { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import api from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Storefront, Money, Package, DownloadSimple } from '@phosphor-icons/react';
import { downloadExport } from '@/lib/exporter';

export const LiveSalesPage = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [sales, setSales] = useState([]);
  const [pharmacies, setPharmacies] = useState([]);
  const [packages, setPackages] = useState([]);
  const [kpis, setKpis] = useState({ totalSales: 0, myShare: 0, pharmacyCount: 0 });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const now = new Date();
      const res = await api.get(`/sales?month=${now.getMonth() + 1}&year=${now.getFullYear()}`);
      const phRes = await api.get('/pharmacies');
      const pkgRes = await api.get('/packages');
      
      setSales(res.data);
      setPharmacies(phRes.data);
      setPackages(pkgRes.data);
      
      const totalSales = res.data.reduce((sum, s) => sum + s.total_amount, 0);
      const myShare = res.data.reduce((sum, s) => sum + s.split.partner_share, 0);
      const pharmacyCount = new Set(res.data.map(s => s.pharmacy_id)).size;
      setKpis({ totalSales, myShare, pharmacyCount });
    } catch (e) {
      console.error(e);
    }
  };

  const getPharmacyName = (id) => pharmacies.find(p => p.id === id)?.name || 'Unknown';
  const getPackageName = (id) => packages.find(p => p.id === id)?.name || 'Unknown';

  return (
    <div className="min-h-screen p-4" style={{ backgroundColor: '#F8FAFC', fontFamily: 'IBM Plex Sans' }}>
      <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
        <h1 className="text-3xl font-bold" style={{ fontFamily: 'Manrope', color: '#1E3A8A' }}>{t('nav.liveSales')}</h1>
        <Button data-testid="export-partner-sales-btn" variant="outline" size="sm" onClick={() => { const now = new Date(); downloadExport(`/exports/sales?month=${now.getMonth() + 1}&year=${now.getFullYear()}`, `MySales_${String(now.getMonth()+1).padStart(2,'0')}-${now.getFullYear()}.xlsx`); }}>
          <DownloadSimple size={14} className="mr-2" /> Export
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card data-testid="partner-total-sales" className="p-6">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs text-slate-600 uppercase tracking-wide">This Month Sales</p>
            <Money size={20} className="text-blue-600" weight="bold" />
          </div>
          <p className="text-3xl font-bold" style={{ fontFamily: 'Manrope', color: '#1E3A8A' }}>RM {kpis.totalSales.toFixed(2)}</p>
        </Card>
        <Card data-testid="partner-my-share" className="p-6">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs text-slate-600 uppercase tracking-wide">My Share</p>
            <Money size={20} className="text-green-600" weight="bold" />
          </div>
          <p className="text-3xl font-bold" style={{ fontFamily: 'Manrope', color: '#0F172A' }}>RM {kpis.myShare.toFixed(2)}</p>
        </Card>
        <Card data-testid="partner-pharmacy-count" className="p-6">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs text-slate-600 uppercase tracking-wide">Pharmacies</p>
            <Storefront size={20} className="text-purple-600" weight="bold" />
          </div>
          <p className="text-3xl font-bold" style={{ fontFamily: 'Manrope' }}>{kpis.pharmacyCount}</p>
        </Card>
      </div>

      <h2 className="text-lg font-bold mb-3" style={{ fontFamily: 'Manrope' }}>Sales Detail</h2>
      <Card className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-100">
            <tr>
              <th className="text-left p-3">Date</th>
              <th className="text-left p-3">Pharmacy</th>
              <th className="text-left p-3">Package</th>
              <th className="text-right p-3">Amount</th>
              <th className="text-right p-3">Your Share</th>
            </tr>
          </thead>
          <tbody>
            {sales.map((s) => (
              <tr key={s.id} className="border-t border-slate-200">
                <td className="p-3">{new Date(s.sale_datetime).toLocaleDateString()}</td>
                <td className="p-3">{getPharmacyName(s.pharmacy_id)}</td>
                <td className="p-3">{getPackageName(s.package_id)}</td>
                <td className="p-3 text-right">RM {s.total_amount?.toFixed(2)}</td>
                <td className="p-3 text-right font-bold text-green-700">RM {s.split.partner_share?.toFixed(2)}</td>
              </tr>
            ))}
            {sales.length === 0 && (
              <tr><td colSpan="5" className="p-6 text-center text-slate-500">No sales this month</td></tr>
            )}
          </tbody>
        </table>
      </Card>
    </div>
  );
};
